import { useCallback, useMemo } from "react";
import type { AnalyticsActions } from "@ifgengineering/client-analytics/src/types";
import useAnalytics from "@helpers/useAnalytics";

/**
 * Fire-and-forget analytics for a single event, mirroring the mobile hook of
 * the same name so a shared event reads identically on both platforms. The
 * argument type is inferred from the event's own props, and a failed call
 * never throws into the component.
 *
 * Use this for action and outcome events; for page views use `useScreenView`.
 */
export const useTrackEvent = <T extends keyof AnalyticsActions>(
  eventName: T
): ((...args: Parameters<AnalyticsActions[T]>) => void) => {
  // `useAnalytics` is an async factory, not a React hook - calling it returns
  // a fresh Promise every render. Memo on `eventName` so the returned callback
  // is stable and a `useEffect` depending on it does not refire every render.
  const pending = useMemo(() => useAnalytics(eventName), [eventName]);

  return useCallback(
    (...args: Parameters<AnalyticsActions[T]>) => {
      Promise.resolve(pending)
        .then((track) =>
          (track as (...trackArgs: Parameters<AnalyticsActions[T]>) => void)(
            ...args
          )
        )
        .catch(() => undefined);
    },
    [pending]
  );
};

export default useTrackEvent;
